import { AuthContext } from "@/contexts/AuthContext";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useContext, useState } from "react";

/**
 * A banner component that shows when the user's Twitch token is invalid.
 * Prompts them to relink their Twitch account via OAuth.
 */
export function TwitchRelinkPrompt() {
    const { twitchTokenInvalid, isAuthenticated } = useContext(AuthContext);
    const [dismissed, setDismissed] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Debug logging
    console.debug("TwitchRelinkPrompt - twitchTokenInvalid:", twitchTokenInvalid);
    console.debug("TwitchRelinkPrompt - isAuthenticated:", isAuthenticated);

    // Only show if user is authenticated, token is invalid, and user hasn't dismissed
    if (!isAuthenticated || !twitchTokenInvalid || dismissed) {
        return null;
    }

    const initiateTwitchOAuth = async () => {
        if (isProcessing) return;

        setIsProcessing(true);
        try {
            console.debug("[Twitch Relink] Starting OAuth initiation...");

            // Get CSRF token from cookies
            const csrfToken = document.cookie
                .split("; ")
                .find((row) => row.startsWith("csrftoken="));

            const url = `${import.meta.env.VITE_VANDERHEIM_NINJA_API_URL}/longhouse-api/village/oauth/twitch/initiate`;
            console.debug("[Twitch Relink] Calling URL:", url);

            // Call backend to get OAuth URL
            const response = await fetch(url, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(csrfToken && {
                        "X-CSRFToken": csrfToken.split("=")[1],
                    }),
                },
            });

            console.debug("[Twitch Relink] Response status:", response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error("[Twitch Relink] Error response:", errorText);
                throw new Error(
                    `Failed to initiate Twitch OAuth: ${response.status}`,
                );
            }

            const data = await response.json();
            console.debug("[Twitch Relink] Response data:", data);

            if (!data.authorization_url) {
                throw new Error("No authorization URL received");
            }

            console.debug(
                "[Twitch Relink] Redirecting to:",
                data.authorization_url,
            );
            // Redirect user to Twitch OAuth page
            window.location.href = data.authorization_url;
        } catch (error) {
            console.error("[Twitch Relink] Error:", error);
            alert(
                "Failed to start Twitch authentication. Please try again or contact support.",
            );
            setIsProcessing(false);
        }
    };

    const handleDismiss = () => {
        setDismissed(true);
    };

    return (
        <div
            className="fixed right-0 bottom-0 left-0 z-40 bg-amber-600 p-4 shadow-lg"
            role="alert"
            aria-live="polite"
        >
            <div className="container mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-white" />
                    <div>
                        <p className="font-semibold text-white">
                            Stream Team Twitch Account Reconnection Required
                        </p>
                        <p className="text-sm text-amber-100">
                            Your Stream Team Twitch authentication has expired
                            or failed. Click "Relink Account" to reconnect.
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={initiateTwitchOAuth}
                        disabled={isProcessing}
                        className="rounded bg-white px-4 py-2 font-semibold text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isProcessing ? "Processing..." : "Relink Account"}
                    </button>
                    <button
                        onClick={handleDismiss}
                        disabled={isProcessing}
                        className="rounded border border-white px-4 py-2 font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
}
